import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useOpenWizard } from "@/lib/wizard-navigation";
import {
  AlertTriangle,
  CalendarPlus,
  Plug,
  CalendarDays,
  Users,
  Link2,
  Pause,
  Play,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SimpleTooltip, TooltipProvider } from "@/components/ui/tooltip";
import {
  StatusBadge,
  conferenceStatusTone,
  destinationLifecycleTone,
  formatJobStatus,
  jobStatusTone,
} from "@/components/status/status-badge";
import { useAuth } from "@/lib/auth-context";
import { api, ApiError } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { getCachedEvents, setCachedEvents } from "@/lib/events-cache";
import type { Conference, Destination } from "@/lib/types";

type DashboardData = {
  health: {
    activeConferences?: number;
    activeStreams: number;
    pausedStreams: number;
    failingStreams: number;
    openFailures: number;
    pastDueConferences: number;
    needsConfiguration?: number;
  };
  conferences: Conference[];
  streams: Destination[];
  failures: Array<{
    deliveryKey: string;
    streamId?: string;
    errorCode?: string;
    remediation?: { title: string; action: string } | null;
  }>;
  recentJobs?: Array<{
    id: string;
    streamId: string;
    status: string;
    errorSummary?: string | null;
    createdAt: number;
    remediation?: { title: string; action: string } | null;
  }>;
  summary?: {
    upcomingConferences: number;
    activeConferences: number;
    needsAttention: number;
    connectedDestinations: number;
  };
  sourceConnections?: Array<{
    id: string;
    provider: string;
    displayName: string;
    email: string;
    externalAccountId: string;
  }>;
};

function formatEpoch(seconds: number | null | undefined): string {
  if (seconds == null) return "Never";
  return new Date(seconds * 1000).toLocaleString();
}

function timeOfDayGreeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

function streamHealthTone(stream: Destination) {
  if (stream.lastErrorCode || stream.lastRunStatus === "failed")
    return "failed" as const;
  if (stream.lastRunStatus === "running" || stream.lastRunStatus === "queued") {
    return "syncing" as const;
  }
  if (stream.lastRunStatus === "succeeded_with_warnings")
    return "warning" as const;
  return destinationLifecycleTone(stream.lifecycle);
}

function streamHealthLabel(stream: Destination): string {
  if (stream.lastErrorCode || stream.lastRunStatus === "failed")
    return "Unhealthy";
  if (stream.lastRunStatus === "running") return "Syncing";
  if (stream.lastRunStatus === "queued") return "Queued";
  if (stream.lastRunStatus === "succeeded_with_warnings") return "Warning";
  if (stream.lifecycle === "active" && stream.lastRunStatus === "succeeded")
    return "Healthy";
  return stream.lifecycle;
}

function StreamRunTooltip({ stream }: { stream: Destination }) {
  const recentRuns = stream.recentRuns ?? [];
  return (
    <div className="w-72 space-y-3 text-left">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="font-medium text-rs-ink-muted">Last Success</p>
          <p className="text-rs-ink">{formatEpoch(stream.lastSuccessAt)}</p>
        </div>
        <div>
          <p className="font-medium text-rs-ink-muted">Last Failure</p>
          <p className="text-rs-ink">{formatEpoch(stream.lastFailureAt)}</p>
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium text-rs-ink-muted">
          Past 5 Runs
        </p>
        {recentRuns.length === 0 ? (
          <p className="text-xs text-rs-ink-muted">No runs yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {recentRuns.map((run) => (
              <li
                key={run.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="text-rs-ink-muted">
                  {formatEpoch(run.ranAt)}
                </span>
                <StatusBadge
                  tone={jobStatusTone(run.status)}
                  className="shrink-0"
                >
                  {formatJobStatus(run.status)}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

type UpcomingEvent = {
  provider: string;
  connectionId: string;
  id: string;
  resourceType: string;
  name: string;
  startTime?: string | null;
  registrantCount?: number | null;
  alreadyConfigured: boolean;
  streamId?: string;
};

function upcomingProviderLabel(provider: string): string {
  switch (provider) {
    case "google_meet":
      return "Google Meet";
    case "google_forms":
      return "Google Forms";
    case "linkedin_events":
      return "LinkedIn Events";
    case "heysummit":
      return "HeySummit";
    case "fluent_forms":
      return "Fluent Forms";
    case "constant_contact":
      return "Constant Contact";
    case "eventbrite":
      return "Eventbrite";
    case "microsoft_teams":
      return "Microsoft Teams";
    default:
      return "Zoom";
  }
}

function sourceProviderIcon(provider: string): React.ReactNode {
  switch (provider) {
    case "google_meet":
    case "google_forms":
      return (
        <svg
          className="h-3 w-3 text-rs-google"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      );
    case "linkedin_events":
      return (
        <svg
          className="h-3 w-3 text-rs-linkedin"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
          />
        </svg>
      );
    case "heysummit":
      return (
        <svg
          className="h-3 w-3 text-rs-heysummit"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
          />
        </svg>
      );
    case "fluent_forms":
      return (
        <svg
          className="h-3 w-3 text-rs-fluent"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v-2H8v2zm0 3h8v-2H8v2zm0 3h5v-2H8v2z"
          />
        </svg>
      );
    case "constant_contact":
      return (
        <svg
          className="h-3 w-3 text-rs-constantcontact"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-7.5c0 .83-.67 1.5-1.5 1.5H7v-3h3.5c.83 0 1.5.67 1.5 1.5zm0-3c0 .83-.67 1.5-1.5 1.5H7v-3h3.5c.83 0 1.5.67 1.5 1.5zm0-3c0 .83-.67 1.5-1.5 1.5H7V7h3.5c.83 0 1.5.67 1.5 1.5z"
          />
        </svg>
      );
    case "mailchimp":
      return (
        <svg
          className="h-3 w-3 text-rs-mailchimp"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M22.5 2.5l-15 15L9 12.5 6 15.5l6 6 16.5-16.5z"
          />
        </svg>
      );
    case "eventbrite":
      return (
        <svg
          className="h-3 w-3 text-rs-eventbrite"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
          />
        </svg>
      );
    case "microsoft_teams":
      return (
        <svg
          className="h-3 w-3 text-rs-microsoft"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
          />
        </svg>
      );
    default:
      return (
        <svg
          className="h-3 w-3 text-rs-zoom"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
          />
        </svg>
      );
  }
}

function sortUpcomingByDateTime(items: UpcomingEvent[]): UpcomingEvent[] {
  return [...items].sort((a, b) => {
    const at = a.startTime ? Date.parse(a.startTime) : Number.MAX_SAFE_INTEGER;
    const bt = b.startTime ? Date.parse(b.startTime) : Number.MAX_SAFE_INTEGER;
    return at - bt;
  });
}

type TimeFilter = "48h" | "1wk" | "1mo";

const TIME_FILTERS: { id: TimeFilter; label: string }[] = [
  { id: "48h", label: "48h" },
  { id: "1wk", label: "1 week" },
  { id: "1mo", label: "1 month" },
];

function filterUpcomingByTimeRange(
  items: UpcomingEvent[],
  filter: TimeFilter,
): UpcomingEvent[] {
  const now = Date.now();
  const windows: Record<TimeFilter, number> = {
    "48h": 48 * 60 * 60 * 1000,
    "1wk": 7 * 24 * 60 * 60 * 1000,
    "1mo": 30 * 24 * 60 * 60 * 1000,
  };
  const maxTime = now + windows[filter];
  return items.filter((ev) => {
    if (!ev.startTime) return false;
    const t = Date.parse(ev.startTime);
    return t >= now && t <= maxTime;
  });
}

export function DashboardPage() {
  const { orgId, canManage } = useOrg();
  const openWizard = useOpenWizard();
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsRefreshing, setEventsRefreshing] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("1wk");
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState<string | null>(null);
  const [createStreamError, setCreateStreamError] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();

  async function createStream(ev: UpcomingEvent) {
    if (!canManage || creatingId) return;
    setCreatingId(`${ev.provider}:${ev.id}`);
    setCreateStreamError(null);
    try {
      const res = await api<{ conferenceId: string }>(
        `/api/accounts/${orgId}/quick-streams`,
        {
          method: "POST",
          body: JSON.stringify({
            provider: ev.provider,
            connectionId: ev.connectionId,
            resourceType: ev.resourceType,
            resourceId: ev.id,
            name: ev.name,
          }),
        },
      );
      navigate(`/conferences/${res.conferenceId}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "DESTINATION_REQUIRED") {
        setCreateStreamError(
          "Connect a CRM destination on Integrations first, then create a stream.",
        );
      } else {
        setCreateStreamError(
          e instanceof Error ? e.message : "Could not create sync stream",
        );
      }
    } finally {
      setCreatingId(null);
    }
  }

  async function patchStreamLifecycle(streamId: string, lifecycle: string) {
    if (!canManage || lifecycleBusy) return;
    const stream = data?.streams.find((s) => s.id === streamId);
    if (!stream?.conferenceId) return;
    setLifecycleBusy(streamId);
    try {
      await api(
        `/api/accounts/${orgId}/conferences/${stream.conferenceId}/streams/${streamId}`,
        { method: "PATCH", body: JSON.stringify({ lifecycle }) },
      );
      setData((prev) =>
        prev
          ? {
              ...prev,
              streams: prev.streams.map((s) =>
                s.id === streamId ? { ...s, lifecycle } : s,
              ),
            }
          : prev,
      );
    } catch {
      // silently ignore — user can retry
    } finally {
      setLifecycleBusy(null);
    }
  }

  const filteredUpcomingEvents = useMemo(
    () => filterUpcomingByTimeRange(upcomingEvents, timeFilter),
    [upcomingEvents, timeFilter],
  );

  useEffect(() => {
    setLoading(true);
    void api<DashboardData>(`/api/accounts/${orgId}/dashboard`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));

    // Stale-while-revalidate: show cached events instantly, then check for updates
    const cached = getCachedEvents(orgId);
    if (cached) {
      setUpcomingEvents(
        sortUpcomingByDateTime(cached.events as UpcomingEvent[]),
      );
      setEventsLoading(false);
      setEventsRefreshing(true);
    }

    void (async () => {
      try {
        // Cheap hash check — avoids full fetch when nothing changed
        const check = await api<{ hash: string | null; count: number }>(
          `/api/accounts/${orgId}/upcoming-events/check`,
        );
        if (check.hash && check.hash === cached?.hash) {
          // No change — keep showing cached data
          return;
        }
        // Hash changed or no cache — do full fetch
        const res = await api<{
          items: UpcomingEvent[];
          hash?: string;
        }>(`/api/accounts/${orgId}/upcoming-events`);
        const sorted = sortUpcomingByDateTime(res.items ?? []);
        setUpcomingEvents(sorted);
        setCachedEvents(orgId, sorted, res.hash ?? check.hash ?? "");
        setEventsError(null);
      } catch {
        if (!cached) {
          setEventsError("Failed to load upcoming events");
        }
        // If we have cache, silently keep showing cached data
      } finally {
        setEventsLoading(false);
        setEventsRefreshing(false);
      }
    })();
  }, [orgId]);

  if (loading) return <PageSkeleton />;

  const conferences = data?.conferences ?? [];
  const streams = data?.streams ?? [];
  const needsDestination = conferences.length > 0 && streams.length === 0;

  const activeConferences =
    data?.summary?.activeConferences ??
    data?.health.activeConferences ??
    conferences.filter((c) => c.status === "active" || c.status === "trial")
      .length;
  const needsAttention =
    data?.summary?.needsAttention ??
    (data?.health.failingStreams ?? 0) + (data?.health.pastDueConferences ?? 0);
  const upcoming =
    data?.summary?.upcomingConferences ??
    conferences.filter((c) => c.status === "draft" || c.status === "trial")
      .length;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="rs-dashboard space-y-6">
        <div className="rs-welcome">
          <div className="rs-welcome-text">
            <h2 className="text-xl font-semibold text-rs-ink">
              {timeOfDayGreeting()},{" "}
              {user?.displayName?.split(" ")[0] || "there"}
            </h2>
            <p className="text-sm text-rs-ink-muted">
              Your events, all in one place.
            </p>
          </div>
        </div>

        <div className="rs-dashboard-stats grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: "Active",
              value: activeConferences,
              icon: CalendarDays,
              to: `/conferences?filter=active`,
            },
            {
              label: "Upcoming / draft",
              value: upcoming,
              icon: CalendarPlus,
              to: `/conferences?filter=upcoming`,
            },
            {
              label: "Needs attention",
              value: needsAttention,
              icon: AlertTriangle,
              to: `/conferences?filter=needs_attention`,
            },
            {
              label: "Connected destinations",
              value: data?.summary?.connectedDestinations ?? streams.length,
              icon: Users,
              to: `/integrations`,
            },
          ].map((stat) => (
            <Link
              key={stat.label}
              to={stat.to}
              className="rs-dashboard-stat transition-colors hover:bg-rs-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rs-info"
              aria-label={`View ${stat.label.toLowerCase()}`}
            >
              <span className="rs-stat-icon">
                <stat.icon aria-hidden="true" />
              </span>
              <div>
                <p className="text-2xl font-semibold text-rs-ink">
                  {stat.value}
                </p>
                <p className="text-sm text-rs-ink-muted">{stat.label}</p>
              </div>
            </Link>
          ))}
        </div>

        {needsDestination ? (
          <EmptyState
            icon={<Plug className="h-8 w-8" />}
            title="Choose Your Destinations"
            description="Connect Zoom, select your event, and choose where your registrants should go."
            actionLabel={canManage ? "Get Started" : undefined}
            onAction={canManage ? () => openWizard() : undefined}
          />
        ) : null}

        {(data?.failures.length ?? 0) > 0 ? (
          <Panel>
            <PanelHeader
              title="Needs Attention"
              description="Sync issues that require action"
              action={
                <Button variant="secondary" size="sm" asChild>
                  <Link to="/reports/activity">View Activity</Link>
                </Button>
              }
            />
            <ul className="divide-y divide-rs-border">
              {data!.failures.slice(0, 5).map((f) => (
                <li
                  key={f.deliveryKey}
                  className="flex items-start gap-3 px-4 py-2 text-sm"
                >
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 text-rs-warning"
                    aria-hidden
                  />
                  <div>
                    <p className="font-medium text-rs-ink">
                      {f.remediation?.title ?? f.errorCode ?? "Sync failure"}
                    </p>
                    <p className="text-rs-ink-muted">
                      {f.remediation?.action ??
                        "Review destination configuration and retry."}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        <Panel>
          <PanelHeader
            className="rs-panel-header-tint"
            title="Upcoming Events"
            description="Events from all connected sources, ordered by date and time"
            titleAside={
              <>
                {data?.sourceConnections &&
                  data.sourceConnections.length > 0 && (
                    <SimpleTooltip
                      content={
                        <div className="w-64 space-y-2 text-left">
                          <p className="text-xs font-medium text-rs-ink-muted">
                            Connected Sources
                          </p>
                          <ul className="space-y-1.5">
                            {data.sourceConnections.map((conn) => (
                              <li
                                key={conn.id}
                                className="flex items-center gap-2 text-xs text-rs-ink"
                              >
                                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rs-muted">
                                  {sourceProviderIcon(conn.provider)}
                                </span>
                                <span>
                                  {upcomingProviderLabel(conn.provider)}
                                  {conn.displayName && (
                                    <span className="text-rs-ink-muted ml-1">
                                      ({conn.displayName})
                                    </span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      }
                      side="right"
                    >
                      <button
                        type="button"
                        className="p-1 text-rs-ink-muted hover:text-rs-ink transition-colors"
                        aria-label="View connected sources"
                      >
                        <Link2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </SimpleTooltip>
                  )}
                <Link
                  to="/upcoming"
                  className="text-sm font-medium text-rs-info hover:underline"
                >
                  [ View All ]
                </Link>
              </>
            }
            action={
              <div className="flex items-center gap-1">
                {TIME_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setTimeFilter(f.id)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      timeFilter === f.id
                        ? "bg-rs-info text-white"
                        : "bg-rs-muted text-rs-ink-muted hover:text-rs-ink"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            }
          />

          {createStreamError ? (
            <div
              className="border-b border-rs-border px-4 py-2 text-xs text-rs-danger"
              role="alert"
            >
              {createStreamError}{" "}
              {createStreamError.includes("destination") ? (
                <Link
                  to="/integrations"
                  className="underline underline-offset-2"
                >
                  Open Integrations
                </Link>
              ) : null}
            </div>
          ) : null}
          <ul className="max-h-80 divide-y divide-rs-border overflow-y-auto">
            {eventsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <li key={i} className="flex items-center gap-3 px-4 py-3">
                    <div className="h-8 w-8 animate-pulse rounded bg-rs-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-2/3 animate-pulse rounded bg-rs-muted" />
                      <div className="h-2 w-1/3 animate-pulse rounded bg-rs-muted" />
                    </div>
                  </li>
                ))
              : filteredUpcomingEvents.slice(0, 8).map((ev) => {
                  const key = `${ev.provider}:${ev.id}`;
                  const busy = creatingId === key;
                  return (
                    <li
                      key={key}
                      className="flex items-center justify-between gap-3 px-4 py-2"
                    >
                      <Link
                        to={`/upcoming`}
                        className="flex min-w-0 flex-1 items-center gap-3 hover:bg-rs-muted"
                      >
                        <span className="rs-conference-label">
                          <CalendarDays aria-hidden="true" />
                          <span className="font-medium text-rs-ink">
                            {ev.name}
                          </span>
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-rs-ink-muted">
                            {upcomingProviderLabel(ev.provider)}
                          </span>
                          {ev.startTime ? (
                            <span className="text-xs text-rs-ink-muted">
                              {new Date(ev.startTime).toLocaleString()}
                            </span>
                          ) : null}
                        </div>
                      </Link>
                      {ev.alreadyConfigured ? (
                        <div className="flex items-center gap-1">
                          {(() => {
                            const stream = ev.streamId
                              ? data?.streams.find((s) => s.id === ev.streamId)
                              : undefined;
                            const isPaused = stream?.lifecycle === "paused";
                            const lifecycleLabel = isPaused
                              ? "Resume Stream"
                              : "Pause Stream";
                            return (
                              <SimpleTooltip content={lifecycleLabel}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={
                                    !canManage || lifecycleBusy === ev.streamId
                                  }
                                  onClick={() =>
                                    ev.streamId &&
                                    void patchStreamLifecycle(
                                      ev.streamId,
                                      isPaused ? "active" : "paused",
                                    )
                                  }
                                  aria-label={lifecycleLabel}
                                >
                                  {isPaused ? (
                                    <Play
                                      className="h-4 w-4 text-rs-success"
                                      fill="currentColor"
                                    />
                                  ) : (
                                    <Pause
                                      className="h-4 w-4 text-rs-danger"
                                      fill="currentColor"
                                    />
                                  )}
                                </Button>
                              </SimpleTooltip>
                            );
                          })()}
                          <SimpleTooltip content="Edit Stream">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={!canManage}
                              onClick={() =>
                                navigate(`/streams?stream=${ev.streamId}`)
                              }
                              aria-label="Edit Stream"
                            >
                              <Pencil
                                className="h-4 w-4 text-yellow-500"
                                fill="currentColor"
                              />
                            </Button>
                          </SimpleTooltip>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          className="shrink-0"
                          disabled={!canManage || busy || Boolean(creatingId)}
                          onClick={() => void createStream(ev)}
                        >
                          {busy ? "Creating…" : "Create Stream"}
                        </Button>
                      )}
                    </li>
                  );
                })}
            {!eventsLoading &&
            eventsError &&
            filteredUpcomingEvents.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-rs-ink-muted">
                {eventsError}
              </li>
            ) : null}
            {!eventsLoading &&
            !eventsError &&
            filteredUpcomingEvents.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-rs-ink-muted">
                {upcomingEvents.length === 0
                  ? "No upcoming events from connected sources."
                  : `No events in the ${TIME_FILTERS.find((f) => f.id === timeFilter)?.label ?? ""} window.`}
              </li>
            ) : null}
          </ul>
        </Panel>

        <Panel>
          <PanelHeader
            className="rs-panel-header-tint"
            title="Sync Streams"
            description="Status, health, and last run for each stream"
            titleAside={
              <Link
                to="/streams"
                className="text-sm font-medium text-rs-info hover:underline"
              >
                [ View All ]
              </Link>
            }
            action={
              canManage && streams.length > 0 ? (
                <Button size="sm" onClick={() => openWizard()}>
                  New Sync Stream
                </Button>
              ) : undefined
            }
          />
          {streams.length === 0 ? (
            <div className="flex min-h-56 items-center justify-center px-5 py-10">
              {canManage ? (
                <Button
                  size="lg"
                  className="rs-cta-flash h-12 px-6 text-base"
                  onClick={() => openWizard()}
                >
                  Add Your First Sync Stream
                </Button>
              ) : (
                <p className="text-sm text-rs-ink-muted">
                  No sync streams yet.
                </p>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-rs-border">
              {streams.slice(0, 8).map((stream) => {
                const detailTo = stream.conferenceId
                  ? `/conferences/${stream.conferenceId}`
                  : "/reports/activity";
                return (
                  <li key={stream.id}>
                    <SimpleTooltip
                      content={<StreamRunTooltip stream={stream} />}
                      side="left"
                    >
                      <Link
                        to={detailTo}
                        className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-rs-muted"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-rs-ink">
                            {stream.name}
                          </p>
                          <p className="truncate text-sm text-rs-ink-muted">
                            {stream.conferenceName
                              ? `${stream.conferenceName} · ${stream.destinationProvider}`
                              : stream.destinationProvider}
                            {" · "}
                            Last run {formatEpoch(stream.lastRunAt)}
                          </p>
                        </div>
                        <StatusBadge tone={streamHealthTone(stream)}>
                          {streamHealthLabel(stream)}
                        </StatusBadge>
                      </Link>
                    </SimpleTooltip>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </TooltipProvider>
  );
}
